import React from "react";
import { MoreHorizontal } from "lucide-react";
import { format } from "date-fns";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  Slider,
} from "@exiftrator/shared/components/ui";
import { HeaderIconButton } from "../../Header/HeaderActions";
import {
  OVERFLOW_MENU_CONTENT_CLASS,
  OVERFLOW_MENU_ITEM_CLASS,
  OVERFLOW_MENU_SHORTCUT_CLASS,
} from "../../Header/overflowMenuStyles";

const DAY_MS = 24 * 60 * 60 * 1000;
const GRANULAR_RANGE_THRESHOLD_MS = 7 * DAY_MS;

function TimelineV3Header({ range }) {
  const rangeDurationMs = range.end.getTime() - range.start.getTime();
  const includeHour = rangeDurationMs <= GRANULAR_RANGE_THRESHOLD_MS;
  const desktopRangeFormat = includeHour ? "MMM d, yy h a" : "MMM d, yyyy";

  return (
    <span className="block min-w-0 truncate tabular-nums">
      {format(range.start, desktopRangeFormat)} -{" "}
      {format(range.end, desktopRangeFormat)}
    </span>
  );
}

function TimelineV3HeaderControls({
  zoomValue,
  zoomMarkers = [],
  onZoomValueChange,
  onToday,
  onFit,
  onOpenShortcuts,
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
      <div className="flex items-center gap-1 rounded-md border border-border-subtle bg-bg-primary p-1">
        <Button variant="ghost" size="sm" onClick={onToday} className="h-7 px-2">
          Today
        </Button>
        <Button variant="ghost" size="sm" onClick={onFit} className="h-7 px-2">
          Fit
        </Button>
      </div>
      <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-primary px-3 py-1.5">
        <span className="text-[10px] text-text-supporting uppercase tracking-wide">
          Scale
        </span>
        <div className="relative w-[240px] lg:w-[300px] pt-1 pb-4">
          <Slider
            value={[zoomValue]}
            min={0}
            max={100}
            step={0.1}
            onValueChange={(nextValue) =>
              typeof nextValue?.[0] === "number" &&
              onZoomValueChange?.(nextValue[0])
            }
            aria-label="Timeline scale"
          />
          <div className="pointer-events-none absolute inset-x-0 top-[6px]">
            {zoomMarkers.map((marker) => (
              <div
                key={`timeline-v3-marker-tick-${marker.id}`}
                className="absolute -translate-x-1/2"
                style={{ left: `${marker.value}%` }}
              >
                <div className="w-px h-2 bg-text-supporting/60" />
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-x-0 top-5 h-3">
            {zoomMarkers.map((marker) => (
              <div
                key={`timeline-v3-marker-label-${marker.id}`}
                className="absolute -translate-x-1/2 text-[10px] leading-none text-text-supporting whitespace-nowrap"
                style={{ left: `${marker.value}%` }}
              >
                {marker.label}
              </div>
            ))}
          </div>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <HeaderIconButton
            intent="utility"
            aria-label="Timeline options"
            title="Timeline options"
          >
            <MoreHorizontal className="size-4" />
          </HeaderIconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={OVERFLOW_MENU_CONTENT_CLASS}>
          <DropdownMenuItem
            onClick={onOpenShortcuts}
            className={OVERFLOW_MENU_ITEM_CLASS}
          >
            <span>Keyboard shortcuts</span>
            <DropdownMenuShortcut className={OVERFLOW_MENU_SHORTCUT_CLASS}>
              ?
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export const TimelineV3HeaderRightControls = React.memo(TimelineV3HeaderControls);
export default React.memo(TimelineV3Header);
